import "@kopi/ui/styles/globals.css";
import type { DevicePlatform } from "@kopi/contracts/codes";
import { createPlatform, detectPlatform, type PlatformKind } from "@kopi/platform";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { StartupError } from "./components/startup-error";
import { ThemePicker, ThemeProvider } from "./components/theme";
import { readLocale } from "./i18n/locale";
import { id } from "./i18n/messages/id";
import { applyDocumentLocale, I18nProvider, loadInitial } from "./i18n/provider";
import { isUnauthenticated } from "./lib/api";
import { markStale, registerServiceWorker } from "./lib/app-update";
import { dismissActiveOverlay, initializeNativeBack } from "./lib/native-back";
import { PlatformProvider } from "./lib/platform";
import { browserStorage } from "./lib/storage";
import { readStorageHealth } from "./lib/storage-health";
import { routeTree } from "./routeTree.gen";

/** Composition root: the one place the runtime platform is detected (PRD ADR-04). */
const PLATFORM_FOR: Record<PlatformKind, DevicePlatform> = {
  web: "web",
  "tauri-android": "android",
  "tauri-ios": "ios",
  "tauri-desktop": "desktop",
};
const platformKind = detectPlatform();

// A 401 anywhere (e.g. the session expired mid-use) sends the user back to sign-in.
function onApiError(error: unknown) {
  if (!isUnauthenticated(error) || router.state.location.pathname === "/login") return;
  queryClient.clear();
  void router.navigate({ to: "/login", search: { redirect: router.state.location.href } });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: (count, error) => !isUnauthenticated(error) && count < 2 },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient, platform: PLATFORM_FOR[platformKind] },
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("#root element missing");

// The chosen language (or the browser's, on a first visit) is loaded before the first render, so the
// app never flashes in the other one. Indonesian is already in the bundle; English is one small chunk.
async function bootstrap(container: HTMLElement) {
  let initial: Awaited<ReturnType<typeof loadInitial>> = { locale: "id", messages: id };
  let runtime: Awaited<ReturnType<typeof createPlatform>> | undefined;
  let failed = false;
  try {
    initial = await loadInitial(readLocale(browserStorage(), navigator.languages));
    // A newer build in another tab upgrading the local database closes this tab's connection (R1-42).
    const initialized = await createPlatform({ onVersionChange: markStale });
    runtime = initialized;
    await initializeNativeBack(
      platformKind,
      () => Promise.resolve(initialized),
      router.history,
      dismissActiveOverlay,
    );
  } catch (error: unknown) {
    failed = true;
    console.error("Failed to initialize the app runtime", error);
  }
  applyDocumentLocale(initial.locale);
  createRoot(container).render(
    <StrictMode>
      <I18nProvider initial={initial}>
        <ThemeProvider>
          {failed || !runtime ? (
            <>
              <div className="fixed top-[max(0.5rem,env(safe-area-inset-top))] right-[max(0.5rem,env(safe-area-inset-right))]">
                <ThemePicker />
              </div>
              <StartupError onRetry={() => window.location.reload()} />
            </>
          ) : (
            <QueryClientProvider client={queryClient}>
              <PlatformProvider platform={runtime}>
                <RouterProvider router={router} />
              </PlatformProvider>
            </QueryClientProvider>
          )}
        </ThemeProvider>
      </I18nProvider>
    </StrictMode>,
  );
}

void bootstrap(root);
// The versioned app-shell cache (R1-42): production web builds only; the Tauri shells bundle their files.
registerServiceWorker(import.meta.env.PROD && platformKind === "web");
// Ask once for storage the browser won't evict, so queued offline sales survive storage pressure.
if (platformKind === "web") void readStorageHealth(undefined, { requestPersistence: true });
